import { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    Avatar,
    Typography,
    IconButton,
    Divider,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import {
    PhotoCamera as PhotoCameraIcon,
    Save as SaveIcon,
    Edit as EditIcon,
} from "@mui/icons-material";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import TopNavBar from "../../components/TopNavBar/DefaultNavBar";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import MDButton from "../../components/MDButton";
import { useCimbeo } from "../../context";

function AccountManagement() {
    const [error, setError] = useState("");
    const { userToken, user, setUser } = useCimbeo();
    const [formData, setFormData] = useState({
        username: user?.username || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        firstName: user?.firstName || "John",
        lastName: user?.lastName || "Doe",
        email: user?.email || "john.doe@example.com",
        phone: user?.phone || "+1 (555) 123-4567",
        organization: user?.organization || "Acme Corp",
        department: user?.department || "Engineering"
    });

    const [profilePhoto, setProfilePhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(user?.photo || null);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                username: user.username || ""
            }));
            if (user.photo) {
                setPhotoPreview(user.photo);
            }
        }
    }, [user]);

    const handleFormChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfilePhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveCredentials = async () => {
        console.log("Saving authentication profile changes:", { formData });
        setError("");
        try {
            // Call AuthN to update the user
            const authURL = `${process.env.REACT_APP_AUTH_URL}/v1/auth/credentials`;
            const resp = await fetch(authURL, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prev_username: user.username,
                    new_username: formData.username,
                    prev_password: formData.currentPassword,
                    new_password: formData.newPassword,
                })
            });
            
            if (!resp.ok) {
                console.error('Failed to update authentication credentials:', resp.status, resp.statusText);
                switch (resp.status) {
                    case 400:
                        throw new Error("Invalid request. Please check your input.");
                    case 401:
                        throw new Error("Invalid password. Could not verify current user.");
                    case 409:
                        throw new Error("Username already in use. Please choose another username.");
                    case 500:
                        throw new Error("Server error. Please try again later.");
                    default:
                        throw new Error("An error occurred. Please try again.");
                }
            }

            setUser({
                id: user.id,
                organization: formData.organization,
                department: formData.department,
                username: formData.username,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
            });
    
            setFormData({
                username: formData.username,
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                organization: formData.organization,
                department: formData.department,
            });

            setOpenDialog(false);
        } catch (error) {
            console.error('Error updating authentication credentials:', error);
            setError(error.message);
        }
        
    };

    const handleSavePersonalInfo = async () => {

        console.log("Saving personal info profile changes:", { formData });
        setError("");
        try {
            // Call personas to update the user
            const personasURL = `${process.env.REACT_APP_PERSONAS_URL}/user`;
            const resp = await fetch(personasURL, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userToken}`
                },
                body: JSON.stringify({
                    user_id: user.id,
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.email,
                    phone_number: formData.phone,
                    organization: formData.organization,
                    job_title: formData.department
                })
            });

            if (!resp.ok) {
                console.error('Failed to update personal information:', resp.status, resp.statusText);
                switch (resp.status) {
                    case 400:
                        throw new Error("Invalid request. Please check your input.");
                    case 401:
                        throw new Error("Unauthorized. Could not verify current user.");
                    case 404:
                        throw new Error("User not found.");
                    case 409:
                        const errorText = await resp.text();
                        if (errorText.includes("email")) {
                            throw new Error("Email already in use. Please choose another email.");
                        } else if (errorText.includes("phone number")) {
                            throw new Error("Phone number already in use. Please choose another phone number.");
                        } else {
                            throw new Error("An error occurred. Please try again.");
                        }
                    case 500:
                        throw new Error("Server error. Please try again later.");
                    default:
                        throw new Error("An error occurred. Please try again.");
                }
            }

            // Update the user context with new values
            setUser({
                id: user.id,
                organization: formData.organization,
                department: formData.department,
                username: formData.username,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
            });

            setOpenDialog(false);
        } catch (error) {
            console.error('Error updating personal information:', error);
            setError(error.message);
        }
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <DashboardLayout>
            <TopNavBar />
            <MDBox>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper variant="navy" sx={{ p: 3 }}>
                            <MDTypography variant="h4" gutterBottom>
                                Account Settings
                            </MDTypography>

                            {/* Profile Photo Section */}
                            <MDBox sx={{ 
                                display: 'flex',
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                py: 4,
                                borderRadius: 2
                            }}>
                                <Avatar
                                    src={photoPreview}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mb: 2,
                                    }}
                                >
                                    {!photoPreview && formData.firstName[0] + formData.lastName[0]}
                                </Avatar>
                                <MDTypography 
                                    variant="h5" 
                                    sx={{ 
                                        mb: 2
                                    }}
                                >
                                    {formData.firstName + " " + formData.lastName}
                                </MDTypography>
                                <MDTypography 
                                    variant="h6" 
                                    sx={{ 
                                        mb: 2
                                    }}
                                >
                                    {"(" + formData.username + ")"}
                                </MDTypography>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="photo-upload"
                                    type="file"
                                    onChange={handlePhotoChange}
                                />
                                <label htmlFor="photo-upload">
                                    <MDButton
                                        variant="contained"
                                        component="span"
                                        startIcon={<PhotoCameraIcon />}
                                        sx={{ 
                                            backgroundColor: 'gold.main',
                                            color: 'dark_teal.main',
                                            '&:hover': {
                                                backgroundColor: 'gold.focus',
                                                borderColor: '#1a3a5f',
                                                color: 'dark_teal.main',
                                            }
                                        }}
                                    >
                                        Change Photo
                                    </MDButton>
                                </label>
                            </MDBox>

                            <Divider sx={{ mb: 2 }} />

                            {/* Personal Information */}
                            <MDBox sx={{ p: 3, borderRadius: 2 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Personal Information
                                </MDTypography>
                                {error && (
                                    <MDBox mb={2}>
                                        <MDTypography variant="caption" color="error" fontWeight="light" textAlign="center" display="block">
                                        {error}
                                        </MDTypography>
                                    </MDBox>
                                )}
                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            value={formData.firstName}
                                            onChange={handleFormChange('firstName')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            value={formData.lastName}
                                            onChange={handleFormChange('lastName')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleFormChange('email')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            value={formData.phone}
                                            onChange={handleFormChange('phone')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Organization"
                                            value={formData.organization}
                                            onChange={handleFormChange('organization')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Department"
                                            value={formData.department}
                                            onChange={handleFormChange('department')}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Update Account Button */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSavePersonalInfo}
                                        sx={{
                                            mr: 2
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<EditIcon />}
                                        onClick={handleOpenDialog}
                                    >
                                        Update Username or Password
                                    </Button>
                                </Box>
                            </MDBox>
                        </Paper>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Account Update Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: 'navy.main', color: 'text.main' }}>Update Account Information</DialogTitle>
                <DialogContent sx={{ backgroundColor: 'navy.main' }}>
                    {error && (
                        <MDBox mb={2}>
                            <MDTypography variant="caption" color="error" fontWeight="light" textAlign="center" display="block">
                            {error}
                            </MDTypography>
                        </MDBox>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Username"
                                    value={formData.username}
                                    onChange={handleFormChange('username')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Current Password"
                                    type="password"
                                    value={formData.currentPassword}
                                    onChange={handleFormChange('currentPassword')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type="password"
                                    value={formData.newPassword}
                                    onChange={handleFormChange('newPassword')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={handleFormChange('confirmPassword')}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: 'navy.main' }}>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button 
                        onClick={handleSaveCredentials}
                        variant="contained"
                        startIcon={<SaveIcon />}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default AccountManagement; 